<template>
    <div class="is-flex is-justify-content-space-around my-2">
        <div style="min-width: 560px; color: #000">
            <h3 class="is-size-6 has-text-centered">Summary</h3>
            <div class="is-flex is-justify-content-space-between mb-4 mt-2">
                <div><b>{{ lastData?.c_name }} - ${{ lastData?.c_adjprice }}</b></div>
                <div>{{ lastData?.industry }} ({{ lastIndustry?.count_of_member_companies }})</div>
                <div><b>var. {{ FormatPercent(lastData?.variance, 1) }}</b></div>
            </div>
            <div class="columns has-text-centered">
                <div class="column">
                    <div>Market Cap</div>
                    <div>${{ FormatSignificant(lastData?.c_mktcap) }} / <span class="has-text-grey">${{ FormatSignificant(lastIndustry?.cap) }}</span></div>
                </div>
                <div class=" column">
                    <div class="has-text-centered">Cash(Debt) & Debt/Ebitda</div>
                    <div class="is-flex is-justify-content-space-around">
                        <div>${{ FormatSignificant(lastData?.c_netdebt) }}</div>
                        <div>{{ FormatDecimal(lastData?.c_netdebt / lastData?.b_ebitda, 1) }}/<span class="has-text-grey">{{ FormatDecimal(lastIndustry?.debt_by_ebitda, 1) }}</span></div>
                    </div>
                </div>
                <div class="column">
                    <div>Rating & Bonds v AA</div>
                    <div class="is-flex is-justify-content-space-around">
                        <div>{{ lastData?.financial_strength }}</div>
                        <div>{{ FormatPercent(lastData?.b_bond_spread) }}</div>
                    </div>
                </div>
            </div>
            <div class="columns has-text-centered">
                <div class="column">
                    <div>Sales Growth (est)</div>
                    <div>{{ FormatPercent(lastData?.c_org_sales, 1) }} / <span class="has-text-grey">{{ FormatPercent(lastIndustry?.best_sales_lt_org, 1) }}</span></div>
                </div>
                <div class="column">
                    <div>P/Sales & 5 yr Median</div>
                    <div class="is-flex is-justify-content-space-around">
                        <div>{{ FormatDecimal(lastData?.c_p_sales) }} / <span class="has-text-grey">{{ FormatDecimal(lastIndustry?.price_by_sales) }}</span></div>
                        <div>{{ FormatDecimal(lastData?.median_of_price_by_sales) }} / <span class="has-text-grey">{{ FormatDecimal(lastIndustry?.median_of_price_by_sales) }}</span></div>
                    </div>
                </div>
                <div class="column">
                    <div>Sigma & 14d RSI</div>
                    <div class="is-flex is-justify-content-space-around">
                        <div>{{ FormatPercent(lastData?.b_SIGMA) }} / <span class="has-text-grey">{{ FormatPercent(lastIndustry?._3d_post_earnings_price_move_0fq) }}</span></div>
                        <div>{{ FormatDecimal(lastData?.b_14d_rsi, 0) }} / <span class="has-text-grey">{{ FormatDecimal(lastIndustry?._14d_rsi, 0) }}</span></div>
                    </div>
                </div>
            </div>
            <div class="columns has-text-centered">
                <div class="column">
                    <div>Gr. & Op. Margin</div>
                    <div class="is-flex is-justify-content-space-around">
                        <div>{{ FormatPercent(lastData?.b_gr_margin, 0) }} / <span class="has-text-grey">{{ FormatPercent(lastIndustry?.best_gross_margin, 0) }}</span></div>
                        <div>{{ FormatPercent(lastData?.b_op_margin, 0) }} / <span class="has-text-grey">{{ FormatPercent(lastIndustry?.best_op_margin, 0) }}</span></div>
                    </div>
                </div>
                <div class="column">
                    <div>P/E ttm & 15 yr Median</div>
                    <div class="is-flex is-justify-content-space-around">
                        <div>{{ FormatDecimal(lastData?.c_pe, 1) }} / <span class="has-text-grey">{{ FormatDecimal(lastIndustry?.p_by_e, 1) }}</span></div>
                        <div>{{ FormatDecimal(lastData?.p_by_e_median15, 1) }}</div>
                    </div>
                </div>
                <div class="column">
                    <div>P/E ntm & 5 yr Median</div>
                    <div class="is-flex is-justify-content-space-around">
                        <div>{{ FormatDecimal(lastData?.c_pe_1yr, 1) }}</div>
                        <div>{{ FormatDecimal(lastData?.p_by_estimate_median5, 1) }}</div>
                    </div>
                </div>
            </div>
            <div class="columns has-text-centered">
                <div class="column">
                    <div>Yield</div>
                    <div>{{ FormatPercent(lastData?.c_yield, 2) }} / <span class="has-text-grey">{{ FormatPercent(lastIndustry?.yield, 2) }}</span></div>
                </div>
                <div class="column">
                    <div>Momentum & Metrics</div>
                    <div class="is-flex is-justify-content-space-around px-1" style="gap: 8px;">
                        <div>{{ FormatDecimal(lastData?.c_momentum_score, 0) }} </div>
                        <div>{{ lastData?.c_bestfit_fields }}</div>
                        <!-- <div>${{ FormatDecimal(lastData?.today_est_revision, 2) }}</div>
                        <div>${{ FormatDecimal(lastData?._3_months_ago, 2) }}</div>
                        <div>${{ FormatDecimal(lastData?._6_months_ago, 2) }}</div> -->
                    </div>
                </div>
                <div class="column">
                    <div>Reversion & Confidence</div>
                    <div class="is-flex is-justify-content-space-around">
                        <div>{{ FormatPercent(lastData?.c_bestfit_reversion, 0) }}</div>
                        <div>{{ FormatDecimal(lastData?.c_bestfit_score, 2) }}</div>
                    </div>
                </div>
            </div>
        </div>
        <div>
            <h3 class="is-size-6 has-text-centered">Earnings</h3>
            <o-table style="font-size: 14px;" table-class="custom research-growth mb-5" :data="releases">
                <o-table-column label="Date" field="latest_announcement_date" v-slot="{ row }">
                    {{ row.qrtfin.q_announce }}
                </o-table-column>
                <o-table-column label="EPS" field="EPS" v-slot="{ row }">
                    {{ FormatDecimal((row.qrtfin.psc_q0 || row.qrtfin.q0) * (maxSplitFactor / row.qrtfin.fr_split), 2) }}
                </o-table-column>
                <o-table-column label="Sales" field="sales" v-slot="{ row }">
                    {{ FormatSignificant(row.qrtfin.sales) }}
                </o-table-column>
                <o-table-column label="KPI" field="KPI" v-slot="{ row }">
                    {{ FormatPercent(row.qrtfin.fr_org_growth, 1) }}
                </o-table-column>
                <o-table-column label="Cash" field="Cash" v-slot="{ row }">
                    {{ FormatSignificant(row.qrtfin.fr_cash) }}
                </o-table-column>
                <o-table-column label="Debt" field="Debt" v-slot="{ row }">
                    {{ FormatSignificant(row.qrtfin.fr_debt) }}
                </o-table-column>
                <o-table-column label="Shares" field="Shares" v-slot="{ row }">
                    {{ FormatSignificant(row.qrtfin.fr_shares * (maxSplitFactor / row.qrtfin.fr_split)) }}
                </o-table-column>
                <o-table-column label="Tax Rate" filed="tax" v-slot="{ row }">
                    {{ FormatPercent(row.annfin?.a_tax_rate / 100, 1) }}
                </o-table-column>
            </o-table>
        </div>
        <div>
            <h3 class="is-size-6 has-text-centered">Long-Term Growth Rates</h3>
            <o-table style="font-size: 14px;" table-class="custom research-growth mb-5" :data="data">
                <o-table-column label="Years" field="year" v-slot="{ row }">
                    {{ row.year }}
                </o-table-column>
                <o-table-column label="Earnings" field="earnings" v-slot="{ row }">
                    {{ FormatPercent(row.earnings, 1) }}
                </o-table-column>
                <o-table-column label="Dividends" field="dividends" v-slot="{ row }">
                    {{ FormatPercent(row.dividends, 1) }}
                </o-table-column>
                <o-table-column label="Total Returns" field="total_return" v-slot="{ row }">
                    {{ FormatPercent(row.total_return, 1) }}
                </o-table-column>
                <o-table-column label="Group" field="industry" v-slot="{ row }">
                    {{ FormatPercent(row.industry, 1) }}
                </o-table-column>
                <o-table-column label="S&P" field="index" v-slot="{ row }">
                    {{ FormatPercent(row.index, 1) }}
                </o-table-column>
            </o-table>
        </div>
    </div>
</template>
<script>
    import { ref, inject, computed, onMounted } from "vue";
    import functions from "@/components/research/functions.js";
    import { max } from "moment";

    export default {
        props: {
            startDate: {
                type: [Date, String, Object],
                default: () => {
                    return new Date(new Date().getFullYear() - 1, 1, 1)
                }
            },
            endDate: {
                type: [Date, String, Object],
                default: () => {
                    return new Date()
                }
            },
            rawData: {
                type: Array,
                default: () => {
                    return [];
                }
            },
            industryData: {
                type: Array,
                default: () => {
                    return [];
                }
            },
            indexData: {
                type: Array,
                default: () => {
                    return [];
                }
            },
            rawReleases: {
                type: Array,
                default: () => {
                    return [];
                }
            }
        },
        setup: (props, context) => {
            const moment = inject("$moment");

            function GetGroupsByYear(yearArr) {
                if (props.rawData.length == 0) return [];

                const arr = [];
                for (const i in yearArr) {
                    const yearsAgo = yearArr[i];

                    const reversed = props.rawData.slice(0).reverse();
                    const reversedIndustry = props.industryData.slice(0).reverse();
                    const reversedIndex = props.indexData.slice(0).reverse();
                    const lastIndex = yearsAgo * 12 - 1;

                    // Check if we have enough data for the requested period
                    // if (lastIndex >= reversed.length || lastIndex >= reversedIndustry.length) {
                    //     continue; // Skip this period if we don't have enough data
                    // }

                    // Ensure current data point exists and has required properties
                    if (!reversed[0] || !reversed[lastIndex] || !reversed[0].b_split || !reversed[lastIndex].b_split) {
                        continue; // Skip if data is incomplete
                    }

                    const latestEarnings = functions.trailing_earnings(reversed[0], reversed[0].b_split);
                    const pastEarnings = functions.trailing_earnings(reversed[lastIndex], reversed[lastIndex].b_split);

                    const latestDividend = functions.dividend(reversed[0], reversed[0].b_split);
                    const pastDividend = functions.dividend(reversed[lastIndex], reversed[lastIndex].b_split);

                    const safeCagr = (latest, past) => {
                        if (latest === undefined || past === undefined) return null;
                        const latestNum = Number(latest);
                        const pastNum = Number(past);
                        if (!Number.isFinite(latestNum) || !Number.isFinite(pastNum) || pastNum <= 0 || latestNum <= 0) {
                            return null;
                        }

                        return Math.pow(latestNum / pastNum, 1 / yearsAgo) - 1;
                    };

                    const cumulativeProduct = (arr) => {
                        if (!arr.length) return null;

                        const values = arr.map(d => Number(functions.total_return(d)));
                        if (values.some(n => !Number.isFinite(n) || n <= 0)) {
                            return null;
                        }

                        const compounded = values.reduce((acc, n) => acc * n, 1);
                        if (compounded <= 0) return null;

                        return Math.pow(compounded, 1 / yearsAgo) - 1;
                    };

                    const obj = {
                        year: yearsAgo,
                        earnings: safeCagr(latestEarnings, pastEarnings),
                        dividends: safeCagr(latestDividend, pastDividend),
                        total_return: cumulativeProduct(reversed.slice(0, lastIndex + 1)),
                        industry: cumulativeProduct(reversedIndustry.slice(0, lastIndex + 1)),
                        index: cumulativeProduct(reversedIndex.slice(0, lastIndex + 1))
                    }

                    arr.push(obj);
                }
                return arr;
            }

            const maxSplitFactor = computed(() => {
                return releases.value[0]?.dividend ?? 1;
            });

            const lastPeriod = computed(() => {
                var index = props.rawData.findIndex(x => x.c_date == props.endDate.format("YYYY-MM-DD"));
                var rtn = props.rawData[index]?.c_period_id || 0;
                return rtn;
            });

            const lastData = computed(() => {
                return props.rawData.find(x => x.c_period_id == lastPeriod.value);
            });

            const lastIndustry = computed(() => {
                if (!props.industryData?.length) return null;

                const normalizePeriod = (value) => {
                    if (value === undefined || value === null || value === "") return null;
                    const numberValue = Number(value);
                    return Number.isNaN(numberValue) ? null : numberValue;
                };

                const targetPeriod = normalizePeriod(lastPeriod.value);

                const candidate = props.industryData.find((item) => {
                    const periodCandidates = [
                        item.i_period_id,
                        item.period_id,
                        item.period,
                        item.periodId
                    ].map(normalizePeriod);

                    return periodCandidates.some((p) => p !== null && p === targetPeriod);
                }) ?? props.industryData[props.industryData.length - 1];

                if (!candidate) return null;

                const normalized = { ...candidate };

                const applyFallback = (targetKey, sourceKeys) => {
                    if (normalized[targetKey] !== undefined && normalized[targetKey] !== null) return;

                    for (const key of sourceKeys) {
                        if (candidate[key] !== undefined && candidate[key] !== null) {
                            normalized[targetKey] = candidate[key];
                            return;
                        }
                    }
                };

                applyFallback("count_of_member_companies", ["i_count"]);
                applyFallback("cap", ["i_mktcap"]);
                applyFallback("debt_by_ebitda", ["i_debt_by_ebitda"]);
                applyFallback("best_sales_lt_org", ["i_org_sales"]);
                applyFallback("price_by_sales", ["i_p_sales"]);
                applyFallback("median_of_price_by_sales", ["i_median_of_price_by_sales", "i_p_sales_median"]);
                applyFallback("_3d_post_earnings_price_move_0fq", ["i_sigma"]);
                applyFallback("_14d_rsi", ["i_14dRSI"]);
                applyFallback("best_gross_margin", ["i_gross_margin"]);
                applyFallback("best_op_margin", ["i_op_margin"]);
                applyFallback("p_by_e", ["i_pe"]);
                applyFallback("yield", ["i_yield"]);

                return normalized;
            });

            const data = computed(() => {
                const yearData = GetGroupsByYear([1, 3, 5, 10, 20]);
                return yearData;
            });

            const releases = computed(() => {
                var currentReleases = props.rawReleases.filter(x => x.qrtfin.q_announce <= props.endDate.format("YYYY-MM-DD"));
                return currentReleases.length >= 6 ? currentReleases.slice(0, 6) : currentReleases;
            });

            function FormatPercent(n, digits = 2) {
                if (!n) return '--';

                const formatter = new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: digits, maximumFractionDigits: digits });
                const result = formatter.format(n);
                return result;
            }

            function FormatDecimal(n, digits = 2) {
                if (!n) return '--';

                const formatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
                const result = formatter.format(n);
                return result;
            }

            function FormatSignificant(n, significant = 4) {
                if (!n || n == 0) return '--';


                const sign = Math.sign(n);
                const abs = Math.abs(n);
                const log = Math.floor(Math.log10(abs));
                const scale = Math.pow(10, log - significant + 1);
                let result = Math.round(abs / scale) * scale;

                var factor = 0;
                while (result > 1000) {
                    result /= 1000;
                    factor++;
                }

                //find number of digits before decimal 
                var digits = Math.floor(Math.log10(result)) + 1;

                return FormatDecimal(result * sign, 4 - digits) + ' ' + GetFactorSuffix(factor);
            }

            function GetFactorSuffix(n) {
                if (n == 0)
                    return 'M';
                if (n == 1)
                    return 'B';
                if (n == 2)
                    return 'T';
                return '';
            }

            return {
                data,
                FormatPercent,
                FormatDecimal,
                FormatSignificant,
                lastData,
                lastIndustry,
                lastPeriod,
                maxSplitFactor,
                releases
            }
        }
    }
</script>
<style>
    .columns {
        border-top: solid 1px #ccc;
        border-bottom: solid 1px #ccc;
        border-collapse: collapse;
    }

    .column {
        padding: 0.3rem !important;
        border-right: solid 1px #ccc;
        border-left: solid 1px #ccc;
    }

    table.research-growth {
        width: 100%;
    }

    table.research-growth td {
        text-align: initial;
        max-width: auto;
        padding-right: initial;
    }
</style>
